/**
 * State management for exam sessions using localStorage for better persistence across tabs
 */

// Keys for localStorage
const KEYS = {
  EXAM_ID: 'examId',
  EXAM_SESSION: 'examSession',
  EXAM_QUESTIONS: 'examQuestions',
  EXAM_RESPONSES: 'examResponses',
  CURRENT_QUESTION_INDEX: 'currentQuestionIndex',
  TIME_SPENT: 'timeSpent',
};

/**
 * Save exam session data to localStorage (exam-specific)
 * @param {Object} session - Exam session data
 * @param {string} examId - Exam ID to scope the storage
 */
const saveExamSession = (session, examId = null) => {
  if (!session) return;
  
  const key = examId ? `${KEYS.EXAM_SESSION}_${examId}` : KEYS.EXAM_SESSION;
  localStorage.setItem(key, JSON.stringify(session));
};

/**
 * Get exam session data from localStorage (exam-specific)
 * @param {string} examId - Exam ID to scope the storage
 * @returns {Object|null} - Exam session object or null if not found
 */
const getExamSession = (examId = null) => {
  const key = examId ? `${KEYS.EXAM_SESSION}_${examId}` : KEYS.EXAM_SESSION;
  const examSession = localStorage.getItem(key);
  return examSession ? JSON.parse(examSession) : null;
};

/**
 * Alias for getExamSession for compatibility with existing code
 * @param {string} examId - Exam ID to scope the storage
 * @returns {Object|null} - Exam session object or null if not found
 */
const getExamData = (examId = null) => {
  return getExamSession(examId);
};

/**
 * Get exam ID from localStorage
 * @returns {string|null} - Exam ID or null if not found
 */
const getExamId = () => {
  return localStorage.getItem(KEYS.EXAM_ID);
};

/**
 * Save exam questions to localStorage
 * @param {Array} questions - Array of question objects
 */
const saveQuestions = (questions) => {
  if (!questions) return;
  localStorage.setItem(KEYS.EXAM_QUESTIONS, JSON.stringify(questions));
};

/**
 * Get exam questions from localStorage
 * @returns {Array|null} - Array of questions or null if not found
 */
const getQuestions = () => {
  const questions = localStorage.getItem(KEYS.EXAM_QUESTIONS);
  return questions ? JSON.parse(questions) : null;
};

/**
 * Save exam responses to localStorage
 * @param {Array} responses - Array of response objects
 */
const saveResponses = (responses) => {
  if (!responses) return;
  localStorage.setItem(KEYS.EXAM_RESPONSES, JSON.stringify(responses));
};

/**
 * Get exam responses from localStorage
 * @returns {Array|null} - Array of responses or null if not found
 */
const getResponses = () => {
  const responses = localStorage.getItem(KEYS.EXAM_RESPONSES);
  return responses ? JSON.parse(responses) : null;
};

/**
 * Save current question index to localStorage (exam-specific)
 * @param {number} index - Current question index
 * @param {string} examId - Exam ID to scope the storage
 */
const saveCurrentQuestionIndex = (index, examId = null) => {
  const key = examId ? `${KEYS.CURRENT_QUESTION_INDEX}_${examId}` : KEYS.CURRENT_QUESTION_INDEX;
  localStorage.setItem(key, index);
};

/**
 * Get current question index from localStorage (exam-specific)
 * @param {string} examId - Exam ID to scope the storage
 * @returns {number} - Current question index or 0 if not found
 */
const getCurrentQuestionIndex = (examId = null) => {
  const key = examId ? `${KEYS.CURRENT_QUESTION_INDEX}_${examId}` : KEYS.CURRENT_QUESTION_INDEX;
  const index = localStorage.getItem(key);
  return index ? parseInt(index, 10) : 0;
};

/**
 * Save time spent on current question
 * @param {number} seconds - Time spent in seconds
 */
const saveTimeSpent = (seconds) => {
  localStorage.setItem(KEYS.TIME_SPENT, seconds);
};

/**
 * Get time spent on current question
 * @returns {number} - Time spent in seconds or 0 if not found
 */
const getTimeSpent = () => {
  const timeSpent = localStorage.getItem(KEYS.TIME_SPENT);
  return timeSpent ? parseInt(timeSpent, 10) : 0;
};

/**
 * Clear all exam data from localStorage
 */
const clearExamData = () => {
  // Clear base keys
  Object.values(KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear exam-specific keys
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith(`${KEYS.CURRENT_QUESTION_INDEX}_`) || key.startsWith(`${KEYS.EXAM_SESSION}_`))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Check if an active exam session exists
 * @returns {boolean} - True if session exists, false otherwise
 */
const hasActiveSession = () => {
  return !!localStorage.getItem(KEYS.EXAM_ID);
};

export default {
  saveExamSession,
  getExamSession,
  getExamData,  // Added the new function
  getExamId,
  saveQuestions,
  getQuestions,
  saveResponses,
  getResponses,
  saveCurrentQuestionIndex,
  getCurrentQuestionIndex,
  saveTimeSpent,
  getTimeSpent,
  clearExamData,
  hasActiveSession,
};
